import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { IconArrowLeft } from '@shared/ui/icons';
import { Button } from '@shared/ui/Button';
import { Input, Select, Label, Field } from '@shared/ui/Input';
import { AddressFields, cleanAddress } from './AddressFields';
import { useAgentPermission } from '@entities/user';
import {
  LOGIST_ORDER_STATUS_LABELS,
  LOGIST_SEGMENT_LABELS,
  type LogistOrder,
  type LogistOrderStatus,
  type LogistPostalAddress,
} from '@entities/logistics';
import {
  useListLogistOrdersQuery,
  useCreateLogistOrderMutation,
  useUpdateLogistOrderMutation,
  useSetLogistOrderStatusMutation,
  useDeleteLogistOrderMutation,
  useExtractLogistOrderMutation,
  useListPtoProjectsQuery,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import type { LogistOrderDraft } from '@entities/logistics';

/**
 * ОСНОВА.Логист — заказы на перевозку (ТЗ AI-LOGIST-2026 §8.1). Ручной ввод,
 * правка, смена статуса, удаление черновика. Импорт XLSX и разбиение на рейсы —
 * следующими инкрементами.
 */

const TEXT_FIELDS: { key: string; label: string; type?: 'date' | 'number' }[] = [
  { key: 'source_order_id', label: 'Внешний № заказа' },
  { key: 'shipper_name', label: 'Грузоотправитель' },
  { key: 'shipper_inn', label: 'ИНН грузоотправителя' },
  { key: 'consignee_name', label: 'Грузополучатель' },
  // ИНН сторон — не формальность: по нему находится ящик ЭДО, которым формат
  // ЭТрН идентифицирует участников. Без ИНН выпуск документа отказывает.
  { key: 'consignee_inn', label: 'ИНН грузополучателя' },
  { key: 'carrier_name', label: 'Перевозчик' },
  { key: 'carrier_inn', label: 'ИНН перевозчика' },
  { key: 'loading_address', label: 'Адрес погрузки' },
  { key: 'unloading_address', label: 'Адрес выгрузки' },
  { key: 'loading_date', label: 'Дата погрузки', type: 'date' },
  // Номер и дата заказа — реквизиты накладной. Отдельно от «внешнего №»: тот
  // служит ключом при импорте и у заказов, заведённых руками, пуст.
  { key: 'order_number', label: '№ заказа (в накладную)' },
  { key: 'order_date', label: 'Дата заказа', type: 'date' },
  { key: 'cargo_name', label: 'Груз' },
  { key: 'planned_mass_kg', label: 'Плановая масса, кг', type: 'number' },
  { key: 'planned_volume_m3', label: 'Плановый объём, м³', type: 'number' },
  // Указания грузоотправителя уходят в накладную дословно, поэтому отдельно
  // от примечания: служебной заметке в юридическом документе не место.
  { key: 'transportation_directives', label: 'Указания по перевозке (в накладную)' },
  { key: 'notes', label: 'Примечание' },
];

const Wrap = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

/** Группа полей адреса — отделена от остальной формы, полей в ней много. */
const AddressBlock = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const AddressTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
  color: ${({ theme }) => theme.colors.text.secondary};
`;
const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;
const Title = styled.h1`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  font-size: 22px;
  font-weight: 600;
`;
const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  max-width: 820px;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;
const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => theme.spacing(2)};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 600;
  white-space: nowrap;
`;
const Td = styled.td`
  padding: ${({ theme }) => theme.spacing(2)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: top;
`;
const Row = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  align-items: center;
`;
const SmallBtn = styled.button`
  font-size: 12px;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;
const FormCard = styled.div`
  padding: ${({ theme }) => theme.spacing(5)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
  max-width: 860px;
`;
const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;
const Msg = styled.div<{ $error?: boolean }>`
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme, $error }) =>
    $error ? theme.colors.status.danger : theme.colors.text.secondary};
`;
const StatusSelect = styled(Select)`
  font-size: 12px;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
`;
const ExtractCard = styled.div`
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
  max-width: 860px;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;
const Textarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  resize: vertical;
  padding: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;
const WarnList = styled.ul`
  margin: ${({ theme }) => theme.spacing(3)} 0 0;
  padding-left: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.warning};
`;

function apiErr(e: unknown): string {
  if (e && typeof e === 'object' && 'data' in e) {
    const d = (e as { data?: unknown }).data;
    if (d && typeof d === 'object' && 'error' in d) return String((d as { error: unknown }).error);
  }
  return 'ошибка запроса';
}

function route(o: LogistOrder): string {
  const from = o.loading_address ?? o.shipper_name ?? '—';
  const to = o.unloading_address ?? o.consignee_name ?? '—';
  return `${from} → ${to}`;
}

function massText(o: LogistOrder): string {
  const parts: string[] = [];
  if (o.planned_mass_kg != null) parts.push(`${o.planned_mass_kg} кг`);
  if (o.planned_volume_m3 != null) parts.push(`${o.planned_volume_m3} м³`);
  return parts.join(' · ') || '—';
}

export function LogistOrders() {
  const { can_read, can_write } = useAgentPermission('logist');
  const { activeProjectId, setActiveProjectId } = useActiveProjectContext();
  const [filterProjectId, setFilterProjectId] = useState(activeProjectId ?? '');
  const [formProjectId, setFormProjectId] = useState(activeProjectId ?? '');
  const [values, setValues] = useState<Record<string, string>>({});
  const [segment, setSegment] = useState('');
  /** Адреса концов маршрута по элементам — их требует формат накладной. */
  const [loadingAddress, setLoadingAddress] = useState<LogistPostalAddress>({});
  const [unloadingAddress, setUnloadingAddress] = useState<LogistPostalAddress>({});
  const [editId, setEditId] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [extractText, setExtractText] = useState('');
  const [warnings, setWarnings] = useState<string[]>([]);

  useEffect(() => {
    if (activeProjectId && !filterProjectId) {
      setFilterProjectId(activeProjectId);
      setFormProjectId(activeProjectId);
    }
  }, [activeProjectId, filterProjectId]);

  const { data: projectsData } = useListPtoProjectsQuery();
  const projects = projectsData?.items ?? [];

  const { data, isLoading } = useListLogistOrdersQuery(
    filterProjectId ? { project_id: filterProjectId } : undefined,
  );
  const [createOrder, { isLoading: creating }] = useCreateLogistOrderMutation();
  const [updateOrder, { isLoading: updating }] = useUpdateLogistOrderMutation();
  const [setStatus] = useSetLogistOrderStatusMutation();
  const [deleteOrder] = useDeleteLogistOrderMutation();
  const [extractOrder, { isLoading: extracting }] = useExtractLogistOrderMutation();

  const orders = data?.orders ?? [];

  function reset() {
    setValues({});
    setSegment('');
    setFormProjectId(filterProjectId || activeProjectId || '');
    setLoadingAddress({});
    setUnloadingAddress({});
    setEditId(null);
    setOk(null);
    setErr(null);
    setExtractText('');
    setWarnings([]);
  }

  /** Пред-заполнить форму нового заказа черновиком из LLM-экстракции (HITL:
   *  человек проверяет и подтверждает; ничего пока не сохранено). */
  function applyDraft(draft: LogistOrderDraft) {
    const v: Record<string, string> = {};
    for (const fld of TEXT_FIELDS) {
      const raw = Reflect.get(draft, fld.key);
      if (raw != null) v[fld.key] = String(raw);
    }
    setValues(v);
    setSegment('');
    setEditId(null);
    setOk(null);
    setErr(null);
  }

  async function runExtract() {
    setErr(null);
    setOk(null);
    setWarnings([]);
    const text = extractText.trim();
    if (!text) {
      setErr('Вставьте текст письма для разбора.');
      return;
    }
    try {
      const res = await extractOrder({ text }).unwrap();
      applyDraft(res.draft);
      setWarnings(res.warnings ?? []);
      setOk('Черновик подставлен в форму ниже — проверьте и подтвердите.');
    } catch (e) {
      setErr(`Не удалось разобрать текст: ${apiErr(e)}`);
    }
  }

  function startEdit(o: LogistOrder) {
    const v: Record<string, string> = {};
    for (const fld of TEXT_FIELDS) {
      const raw = Reflect.get(o, fld.key);
      if (raw != null) v[fld.key] = String(raw);
    }
    setValues(v);
    setSegment(o.segment ?? '');
    setFormProjectId(o.project_id ?? '');
    setLoadingAddress(o.loading_address_details ?? {});
    setUnloadingAddress(o.unloading_address_details ?? {});
    setEditId(o.id);
    setOk(null);
    setErr(null);
  }

  function buildBody(): Record<string, unknown> | null {
    const body: Record<string, unknown> = {};
    for (const fld of TEXT_FIELDS) {
      const raw = (values[fld.key] ?? '').trim();
      if (!raw) continue;
      if (fld.type === 'number') {
        const n = Number(raw);
        if (Number.isNaN(n)) {
          setErr(`Поле «${fld.label}» — число.`);
          return null;
        }
        body[fld.key] = n;
      } else {
        body[fld.key] = raw;
      }
    }
    if (segment) body.segment = segment;
    if (formProjectId) body.project_id = formProjectId;
    body.loading_address_details = cleanAddress(loadingAddress);
    body.unloading_address_details = cleanAddress(unloadingAddress);
    return body;
  }

  async function submit() {
    setErr(null);
    setOk(null);
    const body = buildBody();
    if (!body) return;
    try {
      if (editId) {
        await updateOrder({ id: editId, body }).unwrap();
        setOk('Заказ обновлён.');
      } else {
        await createOrder(body).unwrap();
        setOk('Заказ создан.');
      }
      reset();
    } catch (e) {
      setErr(`Не удалось сохранить: ${apiErr(e)}`);
    }
  }

  async function changeStatus(id: string, status: string) {
    try {
      await setStatus({ id, status }).unwrap();
    } catch (e) {
      setErr(`Не удалось сменить статус: ${apiErr(e)}`);
    }
  }

  async function removeDraft(id: string) {
    try {
      await deleteOrder(id).unwrap();
    } catch (e) {
      setErr(`Не удалось удалить: ${apiErr(e)}`);
    }
  }

  if (!can_read) {
    return (
      <Wrap>
        <div style={{ opacity: 0.7, textAlign: 'center', marginTop: 80 }}>
          Нет доступа к разделу «ОСНОВА.Логист».
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Back to="/agents/logist">
        <IconArrowLeft size={16} />
        <span>ОСНОВА.Логист</span>
      </Back>
      <Title>Заказы на перевозку</Title>
      <Subtitle>
        Внутренние поручения на перевозку (договор поставки с доставкой, заявка покупателя, план
        отгрузки карьера). Плановая масса — план; фактическая фиксируется на рейсе при весовом
        контроле. Разбиение на рейсы и генерация ЭТрН — следующими шагами.
      </Subtitle>

      <Row style={{ marginBottom: 16 }}>
        <Label style={{ margin: 0 }}>Строительный объект:</Label>
        <Select
          value={filterProjectId}
          onChange={(e) => {
            const v = e.target.value;
            setFilterProjectId(v);
            setActiveProjectId(v || null);
            if (!editId) setFormProjectId(v);
          }}
          style={{ maxWidth: 340 }}
        >
          <option value="">— все объекты —</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.code ? `${p.code} — ${p.name}` : p.name}
            </option>
          ))}
        </Select>
      </Row>

      {isLoading ? (
        <div>Загрузка…</div>
      ) : orders.length === 0 ? (
        <div style={{ opacity: 0.7, marginBottom: 24 }}>Заказов пока нет. Создайте первый ниже.</div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Груз</Th>
              <Th>Маршрут</Th>
              <Th>План</Th>
              <Th>Сегмент</Th>
              <Th>Статус</Th>
              {can_write && <Th>Действия</Th>}
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id}>
                <Td>{o.cargo_name ?? '—'}</Td>
                <Td>{route(o)}</Td>
                <Td>{massText(o)}</Td>
                <Td>{o.segment ? o.segment.toUpperCase() : '—'}</Td>
                <Td>
                  {can_write ? (
                    <StatusSelect
                      value={o.status}
                      onChange={(e) => changeStatus(o.id, e.target.value)}
                    >
                      {(Object.keys(LOGIST_ORDER_STATUS_LABELS) as LogistOrderStatus[]).map((s) => (
                        <option key={s} value={s}>
                          {LOGIST_ORDER_STATUS_LABELS[s]}
                        </option>
                      ))}
                    </StatusSelect>
                  ) : (
                    LOGIST_ORDER_STATUS_LABELS[o.status]
                  )}
                </Td>
                {can_write && (
                  <Td>
                    <Row>
                      <SmallBtn onClick={() => startEdit(o)}>Изменить</SmallBtn>
                      {(o.status === 'new' || o.status === 'incomplete') && (
                        <SmallBtn onClick={() => removeDraft(o.id)}>Удалить</SmallBtn>
                      )}
                    </Row>
                  </Td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {can_write && !editId && (
        <ExtractCard>
          <Title style={{ fontSize: 16 }}>Разобрать письмо в черновик</Title>
          <Subtitle style={{ marginBottom: 16 }}>
            Вставьте текст письма или заявки на перевозку — ассистент извлечёт стороны, адреса,
            груз, массу и дату и подставит их в форму ниже. Это черновик: проверьте поля и создайте
            заказ вручную. Персональные контакты (ФИО, телефоны) не извлекаются.
          </Subtitle>
          <Textarea
            value={extractText}
            onChange={(e) => setExtractText(e.target.value)}
            placeholder="Например: ООО «Карьер» отгружает щебень фр. 20–40, 20 тонн, погрузка 20.07.2026, адрес…"
          />
          <Button onClick={runExtract} disabled={extracting}>
            {extracting ? 'Разбираем…' : 'Извлечь черновик'}
          </Button>
          {warnings.length > 0 && (
            <WarnList>
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </WarnList>
          )}
        </ExtractCard>
      )}

      {can_write && (
        <FormCard>
          <Title style={{ fontSize: 16 }}>{editId ? 'Редактирование заказа' : 'Новый заказ'}</Title>
          <FormGrid>
            <Field>
              <Label>Строительный объект (ПТО)</Label>
              <Select value={formProjectId} onChange={(e) => setFormProjectId(e.target.value)}>
                <option value="">— без привязки к объекту —</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code ? `${p.code} — ${p.name}` : p.name}
                  </option>
                ))}
              </Select>
            </Field>
            {TEXT_FIELDS.map((fld) => (
              <Field key={fld.key}>
                <Label>{fld.label}</Label>
                <Input
                  type={fld.type === 'number' ? 'number' : fld.type === 'date' ? 'date' : 'text'}
                  value={values[fld.key] ?? ''}
                  onChange={(e) => setValues((s) => ({ ...s, [fld.key]: e.target.value }))}
                />
              </Field>
            ))}
            <Field>
              <Label>Сегмент перевозки</Label>
              <Select value={segment} onChange={(e) => setSegment(e.target.value)}>
                <option value="">— не задан —</option>
                {Object.entries(LOGIST_SEGMENT_LABELS).map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
          </FormGrid>

          {/* Адреса по элементам: текстовые поля выше остаются ориентиром для
              человека, а в накладную уходит разобранный адрес — свободную
              строку формат принимает лишь комментарием. */}
          <AddressBlock>
            <AddressTitle>Адрес погрузки для накладной</AddressTitle>
            <AddressFields value={loadingAddress} onChange={setLoadingAddress} />
          </AddressBlock>
          <AddressBlock>
            <AddressTitle>Адрес доставки для накладной</AddressTitle>
            <AddressFields value={unloadingAddress} onChange={setUnloadingAddress} />
          </AddressBlock>

          <Row>
            <Button onClick={submit} disabled={creating || updating}>
              {editId ? 'Сохранить' : 'Добавить'}
            </Button>
            {editId && (
              <Button $variant="secondary" onClick={reset}>
                Отмена
              </Button>
            )}
          </Row>
          {ok && <Msg>{ok}</Msg>}
          {err && <Msg $error>{err}</Msg>}
        </FormCard>
      )}
    </Wrap>
  );
}
